<template>
  <div class="bg-white rounded-lg shadow-sm border p-4 sticky top-4">
    <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
      <span class="material-icons">account_tree</span>
      Structure
    </h3>
    
    <!-- Direct Items -->
    <div v-if="checklist.items?.length" class="mb-3 space-y-1">
      <div v-for="item in checklist.items" 
           :key="item.id" 
           class="flex items-center gap-1 py-0.5 text-gray-600 hover:text-gray-900 pl-2">
        <span class="material-icons text-sm">description</span>
        <span class="text-sm truncate">{{ item.description }}</span>
      </div>
    </div>

    <!-- Sections -->
    <div class="space-y-2">
      <div v-for="section in checklist.sections" :key="section.id" class="pl-2">
        <div 
          class="flex items-center gap-1 cursor-pointer py-1 hover:bg-gray-50 rounded"
          @click="toggleSection(section.id)"
        >
          <span class="material-icons text-gray-400 text-sm">
            {{ isExpanded(section.id) ? 'expand_more' : 'chevron_right' }}
          </span>
          <span class="material-icons text-sm">folder</span>
          <span class="text-sm truncate flex-1">{{ section.title || 'Untitled Section' }}</span>
          <span class="text-xs text-gray-500">
            {{ getTotalItems(section) }}
          </span>
        </div>

        <!-- Section Items -->
        <div v-if="isExpanded(section.id)" class="ml-4 space-y-1 mt-1">
          <div v-if="section.items?.length" class="pl-4">
            <div v-for="item in section.items" 
                 :key="item.id" 
                 class="flex items-center gap-1 py-0.5 text-gray-600 hover:text-gray-900">
              <span class="material-icons text-xs">description</span>
              <span class="text-xs truncate">{{ item.description }}</span>
            </div>
          </div>

          <!-- Subsections -->
          <div v-for="subsection in section.subsections" 
               :key="subsection.id" 
               class="pl-4">
            <div 
              class="flex items-center gap-1 cursor-pointer py-1 hover:bg-gray-50 rounded"
              @click="toggleSubsection(subsection.id)"
            >
              <span class="material-icons text-gray-400 text-sm">
                {{ isExpandedSub(subsection.id) ? 'expand_more' : 'chevron_right' }}
              </span>
              <span class="material-icons text-sm">subdirectory_arrow_right</span>
              <span class="text-sm truncate flex-1">{{ subsection.title || 'Untitled Subsection' }}</span>
              <span class="text-xs text-gray-500">{{ subsection.items?.length || 0 }}</span>
            </div>

            <!-- Subsection Items -->
            <div v-if="isExpandedSub(subsection.id)" class="ml-6 space-y-1 mt-1">
              <div v-for="item in subsection.items" 
                   :key="item.id" 
                   class="flex items-center gap-1 py-0.5 text-gray-600 hover:text-gray-900">
                <span class="material-icons text-xs">description</span>
                <span class="text-xs truncate">{{ item.description }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="mt-4 pt-4 border-t text-sm text-gray-500">
      <div class="flex justify-between">
        <span>Total Sections:</span>
        <span>{{ checklist.sections?.length || 0 }}</span>
      </div>
      <div class="flex justify-between">
        <span>Total Items:</span>
        <span>{{ getTotalChecklistItems() }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';

export default {
  props: {
    checklist: {
      type: Object,
      required: true
    }
  },
  setup(props) {  // Add props parameter here
    const expandedSections = ref(new Set());
    const expandedSubsections = ref(new Set());

    const toggleSection = (id) => {
      if (expandedSections.value.has(id)) {
        expandedSections.value.delete(id);
      } else {
        expandedSections.value.add(id);
      }
    };

    const toggleSubsection = (id) => {
      if (expandedSubsections.value.has(id)) {
        expandedSubsections.value.delete(id);
      } else {
        expandedSubsections.value.add(id);
      }
    };

    const isExpanded = (id) => expandedSections.value.has(id);
    const isExpandedSub = (id) => expandedSubsections.value.has(id);

    const getTotalItems = (section) => {
      const directItems = section.items?.length || 0;
      const subsectionItems = section.subsections?.reduce((acc, sub) => 
        acc + (sub.items?.length || 0), 0) || 0;
      return directItems + subsectionItems;
    };

    const getTotalChecklistItems = () => {
      const directItems = props.checklist.items?.length || 0;  // Add props.
      let sectionItems = 0;
      
      if (props.checklist.sections) {  // Add props.
        sectionItems = props.checklist.sections.reduce((total, section) => {  // Add props.
          return total + getTotalItems(section);
        }, 0);
      }
      
      return directItems + sectionItems;
    };

    return {
      toggleSection,
      toggleSubsection,
      isExpanded,
      isExpandedSub,
      getTotalItems,
      getTotalChecklistItems
    };
  }
};
</script>
